<template>
  <div ref="trackRef" class="marquee-text">
    <div ref="measureRef" class="marquee-text__measure" v-html="text"></div>

    <template v-if="overflow">
      <div
        ref="rowRef"
        class="marquee-text__row"
        :style="rowStyles"
      >
        <div class="marquee-text__content" v-html="text"></div>
        <div class="marquee-text__space" :style="{ width: `${spacing}px` }"></div>
        <div class="marquee-text__content" aria-hidden="true" v-html="text"></div>
      </div>
    </template>
    <template v-else>
      <div class="marquee-text__content marquee-text__content--static" :style="staticStyles" v-html="text"></div>
    </template>
  </div>
</template>

<script setup lang="ts">
type Props = {
  text: string
  timeout?: number
  align?: 'left' | 'center' | 'right'
  speedFactor?: number
  repeatTimes?: number | null
}

const props = withDefaults(defineProps<Props>(), {
  timeout: 1000,
  align: 'left',
  speedFactor: 1,
  repeatTimes: null,
})

const trackRef = ref<HTMLElement | null>(null)
const measureRef = ref<HTMLElement | null>(null)
const rowRef = ref<HTMLElement | null>(null)
const overflow = ref(false)
const textWidth = ref(0)
const offset = ref(0)
const isMoving = ref(false)
const baseSpeedPxPerSecond = 80
const spacing = 32
const completedCycles = ref(0)
let cycleTimer: ReturnType<typeof setTimeout> | null = null

const normalizedSpeedFactor = computed(() =>
  Math.min(2, Math.max(0, props.speedFactor ?? 1)),
)

const canRunMarquee = computed(() => normalizedSpeedFactor.value > 0)

const maxCycles = computed(() => {
  if (props.repeatTimes == null) return null
  return Math.max(0, Math.floor(props.repeatTimes)) + 1
})

const hasReachedLimit = computed(() =>
  maxCycles.value !== null && completedCycles.value >= maxCycles.value,
)

const rowStyles = computed(() => {
  const speed = baseSpeedPxPerSecond * normalizedSpeedFactor.value
  const durationSec = speed > 0 ? textWidth.value / speed : 0

  return {
    transform: `translateX(${offset.value}px)`,
    transition: isMoving.value ? `transform ${durationSec}s linear` : 'none',
  }
})

const staticStyles = computed(() => ({
  textAlign: props.align,
}))

const clearCycleTimer = () => {
  if (!cycleTimer) return
  clearTimeout(cycleTimer)
  cycleTimer = null
}

const resetCycle = () => {
  clearCycleTimer()
  isMoving.value = false
  offset.value = 0
}

const scheduleCycle = () => {
  if (!overflow.value || !canRunMarquee.value || hasReachedLimit.value) return

  clearCycleTimer()
  cycleTimer = setTimeout(() => {
    if (!overflow.value || !canRunMarquee.value || hasReachedLimit.value) return
    isMoving.value = true
    offset.value = -(textWidth.value + spacing)
  }, Math.max(props.timeout, 0))
}

const recalc = async () => {
  await nextTick()

  const track = trackRef.value
  const measure = measureRef.value
  if (!track || !measure) return

  const trackWidth = Math.ceil(track.clientWidth)
  const contentWidth = Math.ceil(measure.scrollWidth)
  textWidth.value = contentWidth

  const shouldMarquee = contentWidth > trackWidth
  if (shouldMarquee !== overflow.value) {
    overflow.value = shouldMarquee
    await nextTick()
  }

  completedCycles.value = 0
  resetCycle()
  if (overflow.value && canRunMarquee.value) {
    scheduleCycle()
  }
}

const handleTransitionEnd = (event: TransitionEvent) => {
  if (event.propertyName !== 'transform') return
  if (event.target !== rowRef.value) return

  completedCycles.value += 1
  resetCycle()
  scheduleCycle()
}

onMounted(() => {
  recalc()
  window.addEventListener('resize', recalc)
  rowRef.value?.addEventListener('transitionend', handleTransitionEnd)
})

onBeforeUnmount(() => {
  window.removeEventListener('resize', recalc)
  rowRef.value?.removeEventListener('transitionend', handleTransitionEnd)
  clearCycleTimer()
})

watch(
  () => props.text,
  () => recalc(),
)

watch(
  () => props.timeout,
  () => {
    if (!overflow.value || !canRunMarquee.value) return
    resetCycle()
    scheduleCycle()
  },
)

watch(
  () => props.speedFactor,
  () => {
    if (!overflow.value) return
    resetCycle()
    scheduleCycle()
  },
)

watch(
  () => props.repeatTimes,
  () => {
    if (!overflow.value) return
    completedCycles.value = 0
    resetCycle()
    scheduleCycle()
  },
)

watch(
  () => overflow.value,
  async () => {
    await nextTick()
    rowRef.value?.removeEventListener('transitionend', handleTransitionEnd)
    rowRef.value?.addEventListener('transitionend', handleTransitionEnd)
  },
)
</script>

<style scoped lang="scss">
.marquee-text {
  width: 100%;
  overflow: hidden;
  text-align: left;
  position: relative;
}

.marquee-text__row {
  display: flex;
  align-items: center;
  white-space: nowrap;
  width: max-content;
}

.marquee-text__content {
  display: block;
  width: max-content;
  white-space: nowrap;
}

.marquee-text__content--static {
  width: 100%;
  white-space: nowrap;
}

.marquee-text__measure {
  position: absolute;
  inset: 0 auto auto 0;
  visibility: hidden;
  pointer-events: none;
  white-space: nowrap;
  width: max-content;
}
</style>
